import { useQuery } from "@tanstack/react-query";
import { icons, LucideIcon } from "lucide-react";
import { TranslatedObject } from "./util/sharedModels.ts";
import { doGet, doPost } from "./util/apiHelper.ts";
import { User } from "oidc-client-ts";
import { useAuthenticatedMutation } from "./util/queryHelper.ts";

export type Instrument = TranslatedObject;
export type InstrumentFilter = TranslatedObject;

export type InstrumentWithFilters = Instrument & {
  filters: string[];
};

export type InstrumentWithFiltersAndTranslations = Instrument &
  InstrumentFilters;

export interface InstrumentFilters {
  filters: InstrumentFilter[];
}

export interface InstrumentsWithFiltersRequest {
  instruments: InstrumentWithFiltersAndTranslations[];
}

export interface InstrumentsWithFiltersResponse {
  instruments: InstrumentWithFiltersAndTranslations[];
}

async function postInstrumentsWithFilters(
  user: User,
  request: InstrumentsWithFiltersRequest,
): Promise<InstrumentsWithFiltersResponse> {
  return doPost<InstrumentsWithFiltersResponse>("/instruments", user, request);
}

export function useCreateInstrumentMutation() {
  return useAuthenticatedMutation(
    (
      instrumentWithFiltersAndTranslations: InstrumentsWithFiltersRequest,
      user: User,
    ) => postInstrumentsWithFilters(user, instrumentWithFiltersAndTranslations),
  );
}

async function getSupportedInstruments(): Promise<Instrument[]> {
  return doGet<InstrumentsWithFiltersResponse>("/public/instruments").then(
    (instrumentsResponse) =>
      instrumentsResponse.instruments.map((item) => ({
        name: item.name,
        translations: item.translations,
      })),
  );
}

async function getInstrumentsWithFilters(): Promise<
  InstrumentWithFiltersAndTranslations[]
> {
  return doGet<InstrumentsWithFiltersResponse>("/public/instruments").then(
    (value) => value.instruments,
  );
}

export function useSupportedInstruments() {
  return useQuery({
    queryKey: ["supportedInstruments"],
    queryFn: getSupportedInstruments,
    staleTime: Infinity,
    initialData: [],
  });
}

export function useInstrumentsWithFilters() {
  return useQuery({
    queryKey: ["instrumentsWithFilters"],
    queryFn: getInstrumentsWithFilters,
  });
}

export function getTranslation(
  instrument: TranslatedObject | Instrument,
  lang: string,
) {
  switch (lang) {
    case "en": {
      return instrument.translations.en;
    }
    case "de": {
      return instrument.translations.de;
    }
    default: {
      return instrument.translations.en;
    }
  }
}

export function getTranslations(
  allInstruments: Instrument[],
  instrument: string,
  lang: string,
) {
  const instrumentObject = allInstruments.find(
    (value) => value.name === instrument,
  );
  if (!instrumentObject) return "";
  return getTranslation(instrumentObject, lang);
}

export function getInstrumentIcon(instrument: string): LucideIcon {
  switch (instrument) {
    case "guitar": {
      return icons["Guitar"];
    }
    case "bass": {
      return icons["Guitar"];
    }
    case "piano": {
      return icons["Piano"];
    }
    case "drums": {
      return icons["Drum"];
    }
    case "keyboard": {
      return icons["KeyboardMusic"];
    }
    case "djing": {
      return icons["Turntable"];
    }
    case "singing": {
      return icons["MicVocal"];
    }
    default: {
      return icons["Music"];
    }
  }
}
